// The vault's durable copy, kept in a file on the user's own disk.
//
// WHY A FILE AND NOT BROWSER STORAGE
//
// localStorage, IndexedDB and OPFS all live in the same origin storage bucket.
// Clearing site data removes every one of them - OPFS included, despite the
// name; it is Origin Private, not "on disk" in any sense that survives. A file
// the user holds is outside that bucket entirely.
//
// WHY IT IS SAFE TO WRITE OUT
//
// The contents are encrypted under a key derived from the passkey's PRF output,
// and that key never leaves the authenticator. The file is inert: restoring it
// needs the passkey and a biometric, so a copy in Downloads is not a copy of
// the secret.
//
// WHAT MAKES IT SEAMLESS
//
// The key needs no user involvement at all - the passkey survives wipes and
// syncs across the user's devices, so there is nothing to export, memorise or
// keep. Only the ciphertext needs carrying, and the save happens on the events
// that actually change it rather than on a timer or a nag. Restore is offered
// automatically when the app finds itself with empty storage and a passkey
// available, so the user is never asked to go looking for a menu item.

import { BiometricVault } from "./BiometricVault.js";
import { connectionState, readVaultFile, writeVaultFile } from "./VaultFile.js";

const FILE_NAME = "tide-vault.json";
// Deliberately outside the tide_pref.* namespace: this describes the backup,
// so restoring a backup must not overwrite it with a stale timestamp.
const STATUS_KEY = "tide_vault_backup_status";
const FORMAT = "tide.vault.backup/v1";
const PREFIX = "tide_pref.";

/* ---------------- what goes in ---------------- */

/**
 * Everything needed to bring a wiped device back.
 *
 * Only the `tide_pref.*` entries: the GBRK list, the profile, app-lock
 * settings. The credential record is deliberately NOT included - it holds a
 * credential id that is meaningless on another device, and after a wipe the
 * passkey is found by discovery instead.
 */
function snapshot() {
  const data = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PREFIX)) data[k] = localStorage.getItem(k);
  }
  return data;
}

function restoreSnapshot(data) {
  let written = 0;
  for (const [k, v] of Object.entries(data || {})) {
    // Never let a file write outside the app's own namespace.
    if (!k.startsWith(PREFIX)) continue;
    localStorage.setItem(k, v);
    written++;
  }
  return written;
}

/** Is there anything worth backing up yet? */
export function hasLocalData() {
  return Object.keys(snapshot()).length > 0;
}

/* ---------------- save ---------------- */

/**
 * Write the vault file.
 *
 * On a browser with file handles this rewrites the same file silently once the
 * user has picked it. Without them it produces a download, so it is called only
 * on real changes - never speculatively, because each one costs the user a tap.
 *
 * @returns {Promise<{saved:boolean, transport?:string, name?:string, reason?:string}>}
 */
export async function saveVaultFile({ reuseHandle = true, silent = false } = {}) {
  const data = snapshot();
  if (!Object.keys(data).length) return { saved: false, reason: "nothing to save" };

  const encrypted = await BiometricVault.encryptBlob(JSON.stringify(data));
  const payload = JSON.stringify({
    format: FORMAT,
    // Plaintext metadata is limited to what a user needs to identify the file.
    // Nothing here describes the accounts inside it.
    savedAt: new Date().toISOString(),
    origin: location.origin,
    vault: {
      mode: encrypted.mode,
      iv: Array.from(encrypted.iv),
      ct: Array.from(encrypted.ct),
    },
  }, null, 2);

  const res = await writeVaultFile(payload, { filename: FILE_NAME, reuseHandle, silent });
  noteSaved(res.name);
  return { saved: true, ...res };
}

function noteSaved(name) {
  try {
    localStorage.setItem(STATUS_KEY, JSON.stringify({ at: Date.now(), name }));
  } catch { }
}

/**
 * What the user should be told about their backup.
 *
 * A backup nobody can see is indistinguishable from no backup, and one that
 * silently stopped working is worse than none - so this reports the last
 * successful write and whether the connection still works, and the UI is
 * expected to say so plainly rather than only surfacing failures.
 *
 * `stale` means there is local data newer than the last save could have
 * captured - the honest signal that the file no longer matches the device.
 *
 * @returns {Promise<{state:string, lastSavedAt:number|null, name:string|null, stale:boolean}>}
 */
export async function backupStatus() {
  let last = null;
  try { last = JSON.parse(localStorage.getItem(STATUS_KEY) || "null"); } catch { }

  const state = await connectionState();
  return {
    state,                                   // unsupported | not-connected | needs-permission | ready
    lastSavedAt: last?.at ?? null,
    name: last?.name ?? null,
    // Never saved, but there IS something worth saving.
    stale: !last && hasLocalData(),
  };
}

/**
 * Save without prompting, and only when it can be done silently.
 *
 * Used on the change events. With a file handle already granted this is
 * invisible; without one it does nothing at all rather than throwing a download
 * at someone who never asked for a backup.
 */
export async function saveIfConnected() {
  try {
    const state = await connectionState();
    // Reported rather than swallowed: "needs-permission" is one tap away from
    // working and the UI should say so, while a save that quietly does nothing
    // leaves someone believing they have a backup they do not.
    if (state !== "ready") return { saved: false, reason: state };
    return await saveVaultFile({ reuseHandle: true, silent: true });
  } catch (e) {
    return { saved: false, reason: e?.message || "save failed" };
  }
}

/** Re-export so callers can render the right affordance without guessing. */
export { connectionState };

/* ---------------- restore ---------------- */

/**
 * Read the file back and repopulate storage.
 *
 * Needs a user gesture: both the file picker and the biometric assertion
 * require one.
 *
 * @returns {Promise<{restored:boolean, entries?:number, reason?:string}>}
 */
export async function restoreVaultFile() {
  const file = await readVaultFile({ reuseHandle: true });
  if (!file) return { restored: false, reason: "cancelled" };

  let parsed;
  try {
    parsed = JSON.parse(file.text);
  } catch {
    return { restored: false, reason: "That file is not a Tide vault" };
  }
  if (parsed?.format !== FORMAT || !parsed?.vault) {
    return { restored: false, reason: "That file is not a Tide vault" };
  }

  const plaintext = await BiometricVault.decryptBlob({
    mode: parsed.vault.mode,
    iv: new Uint8Array(parsed.vault.iv),
    ct: new Uint8Array(parsed.vault.ct),
  });

  const entries = restoreSnapshot(JSON.parse(plaintext));
  return { restored: true, entries };
}

/**
 * Should the app offer to restore?
 *
 * True only when there is nothing local to lose. Offering it to a working
 * device invites someone to overwrite a good vault with a stale file.
 */
export function shouldOfferRestore() {
  return !hasLocalData();
}
